import { useEffect } from 'react';

/**
 * HoverPrefetch, invisibly prefetches lazy React Router chunks the moment
 * a user hovers over (or touches) any internal link. Visiting the page becomes
 * near-instant on second click.
 *
 * STRICTLY ADDITIVE: no UI, no markup, no logic changes.
 * Uses `<link rel="prefetch">` with low priority so it never competes with
 * critical resources. Only acts on http(s) anchors that point to same-origin.
 *
 * The Speculation Rules API in index.html handles modern Chrome browsers;
 * this hook covers Firefox, Safari, and older Chrome.
 */
const PREFETCHED = new Set();

const prefetchHref = (href) => {
  if (!href || PREFETCHED.has(href)) return;
  PREFETCHED.add(href);
  try {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  } catch (_e) {
    // ignore
  }
};

const isInternal = (a) => {
  if (!a || !a.href) return false;
  try {
    const u = new URL(a.href);
    if (u.origin !== window.location.origin) return false;
    if (u.pathname.startsWith('/admin')) return false;
    if (u.pathname.startsWith('/store')) return false;
    if (u.pathname.startsWith('/employee')) return false;
    if (u.pathname === window.location.pathname) return false;
    return true;
  } catch {
    return false;
  }
};

const HoverPrefetch = () => {
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (isInternal(a)) prefetchHref(a.href);
    };
    document.addEventListener('mouseover', handler, { passive: true });
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mouseover', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);
  return null;
};

export default HoverPrefetch;
